import React, { useEffect, useState } from 'react';
import {
  IonBackButton,
  IonButtons,
  IonContent,
  IonHeader,
  IonPage,
  IonSpinner,
  IonTitle,
  IonToolbar,
} from '@ionic/react';
import { useHistory, useParams } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';
import GolfIcon from '@/components/GolfIcon';
import { api } from '@/utils/api';
import { useTranslation } from 'react-i18next';

// Interface definitions
interface Game {
  id: number;
  name: string;
  date: string;
  status: string;
  courseName: string;
  clubName: string;
  score?: number;
  scoreDiff?: string; // For displaying "+1", "+8", etc.
  holes: number;
  format: string;
}

interface GamesGroupedByMonth {
  [key: string]: {
    month: string;
    year: string;
    displayName: string;
    games: Game[];
    count: number;
  };
}

const UserGamesPage: React.FC = () => {
  const { userId } = useParams<{ userId: string }>();
  const [gamesData, setGamesData] = useState<Game[]>([]);
  const [loading, setLoading] = useState(true);
  const [groupedGames, setGroupedGames] = useState<GamesGroupedByMonth>({});
  const { t } = useTranslation();
  const history = useHistory();
  // Fetch user's games
  useEffect(() => {
    const loadGames = async () => {
      try {
        setLoading(true);
        const response = await api.get(`/profile/${userId}/games`);
        if (response.data) {
          setGamesData(response.data);
          groupGamesByMonth(response.data);
        }
      } catch (error) {
        console.error('Error fetching user games:', error);
      } finally {
        setLoading(false);
      }
    };

    loadGames();
  }, [userId]);

  // Group games by year and month
  const groupGamesByMonth = (games: Game[]) => {
    const grouped: GamesGroupedByMonth = {};

    games.forEach(game => {
      const date = new Date(game.date);
      const year = date.getFullYear();
      const month = date.getMonth();

      // Create a key in format YYYY-MM
      const key = `${year}-${month.toString().padStart(2, '0')}`;

      // Get month name in user's language
      const monthName = date.toLocaleString(navigator.language, { month: 'long' });

      if (!grouped[key]) {
        grouped[key] = {
          month: monthName,
          year: year.toString(),
          displayName: `${monthName} ${year}`,
          games: [],
          count: 0
        };
      }

      // Add the game to its month group
      grouped[key].games.push(game);
      grouped[key].count += 1;
    });

    // Sort groups by date (newest first)
    const sortedGroups = Object.fromEntries(
      Object.entries(grouped).sort(([keyA], [keyB]) => keyB.localeCompare(keyA))
    );

    setGroupedGames(sortedGroups);
  };

  return (
    <IonPage>
      <IonHeader className="ion-no-border">
        <IonToolbar>
          <IonButtons slot="start">
            <IonBackButton defaultHref={`/user/${userId}`} />
          </IonButtons>
          <IonTitle>{t('played_rounds', 'Games')}</IonTitle>
        </IonToolbar>
      </IonHeader>

      <IonContent className="ion-padding">
        {loading ? (
          <div className="flex justify-center items-center h-full">
            <IonSpinner />
          </div>
        ) : (
          <div className="space-y-6">
            {Object.entries(groupedGames).map(([key, group]) => (
              <div key={key} className="space-y-2">
                <h2 className="text-2xl font-bold text-primary flex items-center">
                  {group.displayName}
                  <span className="ml-2 flex items-center justify-center bg-primary/10 text-primary rounded-full size-7 text-sm">
                    {group.count}
                  </span>
                </h2>

                <div className="bg-card rounded-3xl overflow-hidden">
                  {group.games.map((game, index) => {
                    // Format date for display
                    const gameDate = new Date(game.date);
                    const formattedDate = gameDate.toLocaleDateString(navigator.language, {
                      day: '2-digit',
                      month: '2-digit',
                      year: 'numeric'
                    });

                    // Add divider except for the last item
                    const showDivider = index < group.games.length - 1;

                    return (
                      <div key={game.id} onClick={() => history.push(`/game/${game.id}/result`)}>
                        <div
                          className="block p-4 hover:bg-muted/30 transition-colors"
                        >
                          <div className="flex justify-between items-start">
                            <div className="space-y-1 flex-1">
                              <div className="text-gray-500">{formattedDate}</div>
                              <div className="font-semibold text-lg">{game.clubName}</div>
                              <div>{game.courseName}</div>
                              <div className="flex items-center space-x-2">
                                <span className="text-sm text-gray-500">{game.holes} {t('trous', 'Holes')}</span>

                                {/* Show game format badge */}
                                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-secondary text-secondary-foreground">
                                  {game.format === 'strokeplay' ? 'STK PLAY' : game.format.toUpperCase()}
                                </span>
                              </div>
                            </div>

                            <div className="flex items-center space-x-1">
                              <div className="text-4xl font-bold flex items-center">
                                {game.score || '-'}
                                {game.scoreDiff && (
                                  <span className="text-sm ml-1 text-muted-foreground">{game.scoreDiff}</span>
                                )}
                              </div>
                              <ChevronRight className="text-muted-foreground" />
                            </div>
                          </div>
                        </div>
                        {showDivider && <div className="h-px bg-border mx-4" />}
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}

            {Object.keys(groupedGames).length === 0 && (
              <div className="text-center p-8">
                <img src="/assets/images/notfound.svg" alt="" className="size-64 mx-auto text-muted-foreground mb-4" />
                <h3 className="text-xl font-medium">{t('no_results', 'No games found')}</h3>
                <p className="text-muted-foreground mt-2">
                  {t('no_golf_round_caption')}
                </p>
              </div>
            )}
          </div>
        )}
      </IonContent>
    </IonPage>
  );
};

export default UserGamesPage;
